"use client";

import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCities } from "@lib/redux/slices/citiesSlice"; // Adjust path
import { setProfile, ProfileData } from "@lib/redux/slices/profileSlice"; // Adjust path
import { filterSafeProfileData } from "@utils/profileDataFilter";
import { getTrackerId } from "@utils/utils";
import type { RootState } from "@lib/redux/store"; // Adjust path
import { getUTMParams } from "@utils/utmParams";
import { clearDatadogRumUser, setDatadogRumUser } from "@utils/datadogRum";

interface ClientInitializerProps {
  profile: ProfileData | null;
  userId?: string;
}

const ClientInitializer = ({ profile, userId }: ClientInitializerProps) => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(
    (state: RootState) => state.profile.isAuthenticated
  );
  const currentProfile = useSelector((state: RootState) => state.profile.data);
  const didInit = useRef(false);

  if (typeof window !== "undefined" && profile?.contact_no) {
    sessionStorage.setItem("contact_number", profile?.contact_no);
  }

  useEffect(() => {
    // Run only once on mount
    if (didInit.current) return;
    didInit.current = true;
    getUTMParams();

    if (!isAuthenticated && profile) {
      // Filter profile data before storing in Redux
      const safeProfile = filterSafeProfileData(profile);
      dispatch(setProfile(safeProfile));
    }
  }, []);

  useEffect(() => {
    const rumProfile = currentProfile || profile;
    const trackerId = getTrackerId();

    if (rumProfile) {
      const rumUserId = userId || rumProfile.ekey || trackerId || undefined;
      const rumUser = {
        id: rumUserId,
        name: rumProfile.name || undefined,
        email: rumProfile.email || undefined,
        auth_user_id: userId || undefined,
        ekey: rumProfile.ekey || undefined,
        tracker_id: trackerId || undefined,
        is_onboarded: rumProfile.is_onboarded,
        completion_percentage: rumProfile.completionPercentage,
      };

      setDatadogRumUser(rumUser);
    } else {
      if (!trackerId) {
        clearDatadogRumUser();
        return;
      }

      setDatadogRumUser({
        id: trackerId,
        tracker_id: trackerId,
      });
    }
  }, [currentProfile, profile, userId]);

  return null;
};

export default ClientInitializer;
